import React, { useState } from 'react';
import Input from '../ui/Input';
import Button from '../ui/Button';

const InitialSetupForm = ({ onSubmit, loading = false }) => {
  const [formData, setFormData] = useState({
    currentAge: '',
    retirementYear: '',
    projectionHorizon: ''
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Allow only numbers
    if (value && !/^\d+$/.test(value)) return;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const currentYear = new Date().getFullYear();

    // Validate current age
    if (!formData.currentAge) {
      newErrors.currentAge = 'Current age is required';
    } else {
      const age = parseInt(formData.currentAge);
      if (age < 18 || age > 100) {
        newErrors.currentAge = 'Age must be between 18 and 100';
      }
    }

    // Validate retirement year
    if (!formData.retirementYear) {
      newErrors.retirementYear = 'Retirement year is required';
    } else {
      const retirementYear = parseInt(formData.retirementYear);
      const currentAge = parseInt(formData.currentAge) || 0;
      
      if (retirementYear < currentYear) {
        newErrors.retirementYear = 'Retirement year cannot be in the past';
      } else if (retirementYear > currentYear + 50) {
        newErrors.retirementYear = 'Retirement year cannot be more than 50 years in the future';
      }
      
      // Check if retirement year makes sense with current age
      const yearsToRetirement = retirementYear - currentYear;
      if (currentAge + yearsToRetirement > 100) {
        newErrors.retirementYear = 'Retirement age would be over 100 years';
      }
    }

    // Validate projection horizon
    if (!formData.projectionHorizon) {
      newErrors.projectionHorizon = 'Projection horizon is required';
    } else {
      const horizon = parseInt(formData.projectionHorizon);
      
      // Allow all users to set 1-100 years
      // Free users will see truncated data in charts
      if (horizon < 1 || horizon > 100) {
        newErrors.projectionHorizon = 'Projection horizon must be between 1 and 100 years';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit({
        currentAge: parseInt(formData.currentAge),
        retirementYear: parseInt(formData.retirementYear),
        projectionHorizon: parseInt(formData.projectionHorizon)
      });
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-foreground mb-4">
          Welcome! Let's Set Up Your Retirement Plan
        </h1>
        <p className="text-muted-foreground">
          Please provide some basic information to get started. This data will be used in both Quick and Precision modes.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          type="number"
          name="currentAge"
          label="Current Age"
          required
          value={formData.currentAge}
          onChange={handleInputChange}
          error={errors.currentAge}
          description="Must be between 18 and 100"
          min="18"
          max="100"
        />

        <Input
          type="number"
          name="retirementYear"
          label="Retirement Year"
          required
          value={formData.retirementYear}
          onChange={handleInputChange}
          error={errors.retirementYear}
          description="Year when you plan to retire"
          min={new Date().getFullYear()}
          max={new Date().getFullYear() + 50}
        />

        <Input
          type="number"
          name="projectionHorizon"
          label="Projection Horizon (Years)"
          required
          value={formData.projectionHorizon}
          onChange={handleInputChange}
          error={errors.projectionHorizon}
          description="Number of years to project forward (1-100)"
          min="1"
          max="100"
        />

        <div className="pt-4">
          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={loading}
          >
            Complete Setup & Start Planning
          </Button>
        </div>
      </form>
    </div>
  );
};

export default InitialSetupForm;

